import { getIntl } from 'umi';

import { selectCommercialBillingApiRoute } from '@/components/SiderMenu/utils/sessionNavigation';
import { initialPagination, initialState, makeListReducers } from '@/models/utils';
import { bindServices } from '@/services/api';
import CoreQueries from '@/utils/input/queries';

import {
  ADMINISTRATION_API_ROUTE,
  DEFAULT_ORDER_BY,
  MODEL_NAME,
  NAVIGATION_KEY,
  SUBSCRIPTION_ADMINISTRATION_ACTION,
} from './constants';
import {
  filtersConfig,
  getSubscriptionAdministrationActionFlags,
  isSameSubscriptionPlan,
  mergeBillingSubscriptionAdministration,
} from './utils';

const api = bindServices(MODEL_NAME);
const pagedQuery = CoreQueries.BillingSubscription.paged;

function* loadAdministrationProjection(id, call, put) {
  const res = yield call(api.getById, {
    route: ADMINISTRATION_API_ROUTE,
    id,
    readPriority: true,
  });
  const administration = Array.isArray(res) ? res[0] : res;
  yield put({ type: 'queryAdministration', payload: administration });
  return administration;
}

/** Pattern X — hand-rolled read-only model; API base from commercial billing session navigation. */
const moduleInitialState = {
  ...initialState,
  list: [],
  orderBy: DEFAULT_ORDER_BY,
  select: pagedQuery.select,
  searchSelect: pagedQuery.searchSelect,
  showForm: false,
  detail: false,
  id: null,
  item: null,
  success: false,
  activatingItemIds: {},
  administrationById: {},
  administrationLoading: false,
  administrationTransitioning: false,
  administrationModal: {
    state: false,
    id: null,
    action: null,
    subscription: null,
  },
  changePlanModal: {
    state: false,
    step: 'select',
    id: null,
    subscription: null,
    plans: [],
    plansLoading: false,
    selectedPlanId: null,
    submitting: false,
  },
  filtersConfig,
};

const unwrapEligiblePlans = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const isForbiddenChangePlan = (error) => {
  const status = error?.status ?? error?.statusCode ?? error?.Status;
  const code = error?.errorCode ?? error?.code ?? error?.ErrorCode ?? error?.Code;
  return status === 403 || code === 403 || code === '403';
};

function* loadEligiblePlans(id, call) {
  const response = yield call(
    api.getById,
    {
      route: ADMINISTRATION_API_ROUTE,
      id: `${id}/plans`,
      readPriority: true,
    },
    { suppressErrorNotification: true },
  );
  return unwrapEligiblePlans(response);
}

const BillingSubscriptionsModel = {
  namespace: MODEL_NAME,
  state: moduleInitialState,

  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const fetchRoute = yield* selectCommercialBillingApiRoute(NAVIGATION_KEY, select);
        if (!fetchRoute) return;

        const pagination = yield select((state) => state[MODEL_NAME].pagination);
        const pageSize = yield select((state) => state.global.pageSize);
        const res = yield call(api.getAll, {
          route: `${fetchRoute}/paged`,
          params: {
            limit: pageSize,
            ...pagedQuery,
            ...pagination,
            ...payload,
          },
          readPriority: true,
        });

        const items = res?.items || res?.data || [];
        yield put({
          type: 'queryData',
          payload: {
            ...res,
            items,
            limit: pageSize,
            page: payload.page,
            totalCount: res?.totalCount ?? res?.count ?? res?.total ?? items.length,
          },
        });
      } catch (e) {
        /*  */
      }
    },

    *fetchItem({ payload }, { call, put, select }) {
      try {
        const fetchRoute = yield* selectCommercialBillingApiRoute(NAVIGATION_KEY, select);
        if (!fetchRoute) return;

        yield put({ type: 'success', payload: false });
        const res = yield call(api.getById, {
          route: fetchRoute,
          id: payload.id,
          params: {
            ...(payload.detail ? CoreQueries.BillingSubscription?.itemDetail : {}),
            ...(payload?.params && { ...payload.params }),
          },
        });
        const rawItem = Array.isArray(res) ? res[0] : res;
        let administration = yield select(
          (state) => state[MODEL_NAME].administrationById?.[payload.id],
        );
        if (payload.detail) {
          try {
            administration = yield* loadAdministrationProjection(payload.id, call, put);
          } catch (administrationError) {
            if (administrationError?.message) {
              yield put({
                type: 'global/showNotificationsGlobal',
                payload: { visible: true, type: 'error', message: administrationError.message },
              });
            }
          }
        }
        const item = mergeBillingSubscriptionAdministration(rawItem, administration);
        yield put({ type: 'queryItem', payload: item });
      } catch (e) {
        /*  */
      }
    },

    *fetchAdministration({ payload }, { call, put }) {
      const id = payload?.id;
      if (!id) return;

      yield put({ type: 'setAdministrationLoading', payload: true });
      try {
        yield* loadAdministrationProjection(id, call, put);
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      } finally {
        yield put({ type: 'setAdministrationLoading', payload: false });
      }
    },

    *openAdministrationAction({ payload }, { call, put, select }) {
      const { subscription, action } = payload || {};
      const id = subscription?.id;
      const isSuspend = action === SUBSCRIPTION_ADMINISTRATION_ACTION.SUSPEND;
      const isReactivate = action === SUBSCRIPTION_ADMINISTRATION_ACTION.REACTIVATE;
      if (!id || (!isSuspend && !isReactivate)) return;

      yield put({ type: 'setAdministrationLoading', payload: true });
      try {
        let administration = yield select((state) => state[MODEL_NAME].administrationById?.[id]);
        if (!administration) {
          administration = yield* loadAdministrationProjection(id, call, put);
        }

        const flags = getSubscriptionAdministrationActionFlags({
          ...administration,
          administrationLoaded: true,
        });
        if ((isSuspend && !flags.canSuspend) || (isReactivate && !flags.canReactivate)) {
          return;
        }

        yield put({
          type: 'queryAdministrationModal',
          payload: {
            state: true,
            id,
            action,
            subscription: mergeBillingSubscriptionAdministration(subscription, administration),
          },
        });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      } finally {
        yield put({ type: 'setAdministrationLoading', payload: false });
      }
    },

    *openChangePlan({ payload }, { call, put }) {
      const id = payload?.id;
      if (!id) return;

      yield put({
        type: 'queryChangePlanModal',
        payload: {
          state: true,
          step: 'select',
          id,
          subscription: payload.subscription || null,
          plans: [],
          plansLoading: true,
          selectedPlanId: null,
          submitting: false,
        },
      });

      try {
        const plans = yield* loadEligiblePlans(id, call);
        yield put({
          type: 'queryChangePlanModal',
          payload: { plans, plansLoading: false },
        });
      } catch (e) {
        yield put({ type: 'resetChangePlanModal' });
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
    },

    *confirmChangePlanSelection(_, { put, select }) {
      const modal = yield select((state) => state[MODEL_NAME].changePlanModal);
      const selectedPlan = (modal?.plans || []).find(
        (plan) => String(plan?.id) === String(modal?.selectedPlanId),
      );
      if (!modal?.selectedPlanId || modal.plansLoading || modal.submitting) return;
      if (isSameSubscriptionPlan(modal?.subscription, modal.selectedPlanId, selectedPlan?.name)) {
        return;
      }
      yield put({ type: 'queryChangePlanModal', payload: { step: 'confirm' } });
    },

    *backToChangePlanSelect(_, { put, select }) {
      const modal = yield select((state) => state[MODEL_NAME].changePlanModal);
      if (modal?.submitting) return;
      yield put({ type: 'queryChangePlanModal', payload: { step: 'select' } });
    },

    *closeChangePlan(_, { put, select }) {
      const modal = yield select((state) => state[MODEL_NAME].changePlanModal);
      if (modal?.submitting) return;
      yield put({ type: 'resetChangePlanModal' });
    },

    *submitChangePlan(_, { call, put, select }) {
      const modal = yield select((state) => state[MODEL_NAME].changePlanModal);
      const planId = modal?.selectedPlanId;
      const selectedPlan = (modal?.plans || []).find((plan) => String(plan?.id) === String(planId));
      if (!modal?.id || !planId || modal.submitting || modal.plansLoading) return;
      if (isSameSubscriptionPlan(modal?.subscription, planId, selectedPlan?.name)) return;

      yield put({ type: 'queryChangePlanModal', payload: { submitting: true } });
      try {
        const res = yield call(
          api.apiRoute,
          {
            route: `${ADMINISTRATION_API_ROUTE}/${modal.id}/plan`,
            method: 'post',
            data: { planId },
          },
          { suppressErrorNotification: true },
        );
        const administration = Array.isArray(res) ? res[0] : res;
        yield put({ type: 'queryAdministration', payload: administration });
        yield put({ type: 'resetChangePlanModal' });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
            message: getIntl().formatMessage({ id: 'billingSubscriptions.changePlan.success' }),
          },
        });
        const currentId = yield select((state) => state[MODEL_NAME].id);
        if (String(currentId || '') === String(modal.id)) {
          yield put({ type: 'fetchItem', payload: { id: modal.id, detail: true } });
        }
        yield put({ type: 'fetchList', payload: {} });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
        if (isForbiddenChangePlan(e)) {
          yield put({ type: 'resetChangePlanModal' });
          return;
        }
        yield put({
          type: 'queryChangePlanModal',
          payload: { submitting: false, step: 'select', plansLoading: true },
        });
        try {
          const plans = yield* loadEligiblePlans(modal.id, call);
          const stillThere = plans.some((plan) => String(plan?.id) === String(planId));
          yield put({
            type: 'queryChangePlanModal',
            payload: {
              plans,
              plansLoading: false,
              selectedPlanId: stillThere ? planId : null,
            },
          });
        } catch (e) {
          yield put({
            type: 'queryChangePlanModal',
            payload: { plans: [], plansLoading: false, selectedPlanId: null },
          });
        }
        const currentId = yield select((state) => state[MODEL_NAME].id);
        if (String(currentId || '') === String(modal.id)) {
          yield put({ type: 'fetchItem', payload: { id: modal.id, detail: true } });
        }
      }
    },

    *closeAdministrationModal(_, { put }) {
      yield put({
        type: 'queryAdministrationModal',
        payload: { state: false, id: null, action: null, subscription: null },
      });
    },

    *submitAdministrationAction({ payload }, { call, put, select }) {
      const { id, action } = payload || {};
      const isSuspend = action === SUBSCRIPTION_ADMINISTRATION_ACTION.SUSPEND;
      const isReactivate = action === SUBSCRIPTION_ADMINISTRATION_ACTION.REACTIVATE;
      const reason = String(payload?.reason || '').trim();
      if (!id || (!isSuspend && !isReactivate)) return;
      if (isSuspend && (!reason || reason.length > 1000)) return;

      yield put({ type: 'setAdministrationTransitioning', payload: true });
      try {
        const request = {
          route: `${ADMINISTRATION_API_ROUTE}/${id}/${action}`,
          method: 'post',
        };
        if (isSuspend) {
          request.data = { reason };
        }
        const res = yield call(api.apiRoute, request);
        const administration = Array.isArray(res) ? res[0] : res;
        yield put({ type: 'queryAdministration', payload: administration });
        yield put({
          type: 'queryAdministrationModal',
          payload: { state: false, id: null, action: null, subscription: null },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });

        const currentId = yield select((state) => state[MODEL_NAME].id);
        if (String(currentId || '') === String(id)) {
          yield put({ type: 'fetchItem', payload: { id, detail: true } });
        }
        yield put({ type: 'fetchList', payload: {} });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
        yield put({
          type: 'queryAdministrationModal',
          payload: { state: false, id: null, action: null, subscription: null },
        });
        yield put({ type: 'fetchAdministration', payload: { id } });
        const currentId = yield select((state) => state[MODEL_NAME].id);
        if (String(currentId || '') === String(id)) {
          yield put({ type: 'fetchItem', payload: { id, detail: true } });
        }
      } finally {
        yield put({ type: 'setAdministrationTransitioning', payload: false });
      }
    },

    *setOrderBy({ payload }, { put }) {
      yield put({
        type: 'queryOrderBy',
        payload,
      });
    },

    *showForm({ payload }, { put }) {
      yield put({
        type: 'queryShowForm',
        payload,
      });
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },
  },

  reducers: {
    ...makeListReducers({
      initialPagination,
      initialState: moduleInitialState,
    }),
    setFiltersConfig(state, { payload }) {
      return {
        ...state,
        filtersConfig: payload,
      };
    },
    queryAdministration(state, { payload }) {
      if (!payload?.id) return state;
      const id = payload.id;
      return {
        ...state,
        administrationById: {
          ...state.administrationById,
          [id]: payload,
        },
        item:
          String(state.item?.id || '') === String(id)
            ? mergeBillingSubscriptionAdministration(state.item, payload)
            : state.item,
        list: (state.list || []).map((item) =>
          String(item?.id || '') === String(id)
            ? mergeBillingSubscriptionAdministration(item, payload)
            : item,
        ),
      };
    },
    queryAdministrationModal(state, { payload }) {
      return {
        ...state,
        administrationModal: {
          state: !!payload?.state,
          id: payload?.id ?? null,
          action: payload?.action ?? null,
          subscription: payload?.subscription ?? null,
        },
      };
    },
    setAdministrationLoading(state, { payload }) {
      return { ...state, administrationLoading: !!payload };
    },
    setAdministrationTransitioning(state, { payload }) {
      return { ...state, administrationTransitioning: !!payload };
    },
    queryChangePlanModal(state, { payload }) {
      return {
        ...state,
        changePlanModal: {
          ...state.changePlanModal,
          ...payload,
        },
      };
    },
    resetChangePlanModal(state) {
      return {
        ...state,
        changePlanModal: moduleInitialState.changePlanModal,
      };
    },
  },
};

export default BillingSubscriptionsModel;
